import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { X, QrCode, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import api from '../../../utils/api';

const ZELLE_QR_URL = 'https://aone-universe.s3.us-east-1.amazonaws.com/parent-portal/QR-code/ZELLE_QR_Business.png';
const ZELLE_ID = 'aoneinstituteoffice@gmail.com';

// Inner component that holds modal state - remounts when modal opens for fresh state
function ZelleModalContent({
  onClose,
  onConfirm,
  totalAmount,
  isLoading,
  studentId,
  studentCourseIds,
  allCoursesAlreadySubmitted,
}) {
  const [step, setStep] = useState(1);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(false);

  const handleNextStep = async () => {
    setQrLoading(true);

    // Notify backend of QR view (fire-and-forget, don't block UI)
    if (studentId && studentCourseIds?.length > 0) {
      try {
        await api.post(`/parent/children/${studentId}/payments/zelle-qr-viewed`, {
          studentCourseIds,
        });
      } catch (error) {
        console.error('Failed to track QR view:', error);
        // Don't block user - this is just for notification
      }
    }

    // Brief delay for visual feedback
    setTimeout(() => {
      setStep(2);
      setQrLoading(false);
    }, 300);
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <div className="flex items-center justify-center p-4 h-full">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <QrCode className="w-5 h-5 text-[#6d1ed4]" />
            <h2 className="text-lg font-semibold">Pay with Zelle</h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicator */}
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full transition-colors ${step === 1 ? 'bg-[#6d1ed4]' : 'bg-gray-300 dark:bg-gray-600'}`} />
              <div className={`w-2 h-2 rounded-full transition-colors ${step === 2 ? 'bg-[#6d1ed4]' : 'bg-gray-300 dark:bg-gray-600'}`} />
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto">
          {step === 1 ? (
            /* Step 1: Instructions */
            <div className="space-y-4">
              {/* Amount */}
              <div className="bg-[#6d1ed4]/10 dark:bg-[#6d1ed4]/20 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Amount to Pay</p>
                <p className="text-2xl font-bold text-[#6d1ed4]">${totalAmount.toFixed(2)}</p>
              </div>

              {/* Instructions */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white">How to Pay with Zelle</h3>
                <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li>Open your banking app and navigate to Zelle</li>
                  <li>Scan our QR code or use our Zelle ID</li>
                  <li>Send the exact amount: <strong className="text-[#6d1ed4]">${totalAmount.toFixed(2)}</strong></li>
                  <li>Return here and confirm your payment</li>
                </ol>
              </div>

              {/* Note */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>Note:</strong> Your enrollment will be confirmed once our office verifies the payment. This usually takes 1-2 business days.
                </p>
              </div>
            </div>
          ) : (
            /* Step 2: QR Code */
            <div className="space-y-4">
              {/* Amount reminder */}
              <div className="bg-[#6d1ed4]/10 dark:bg-[#6d1ed4]/20 rounded-lg p-3 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Send exactly <span className="font-bold text-[#6d1ed4]">${totalAmount.toFixed(2)}</span>
                </p>
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center">
                {ZELLE_QR_URL && !qrError ? (
                  <img
                    src={ZELLE_QR_URL}
                    alt="Zelle QR Code"
                    className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700"
                    onError={() => setQrError(true)}
                  />
                ) : (
                  <div className="w-64 h-64 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex flex-col items-center justify-center p-4">
                    <QrCode className="w-12 h-12 text-amber-400 dark:text-amber-500 mb-3" />
                    <p className="text-sm text-amber-700 dark:text-amber-400 text-center">
                      QR code unavailable.
                      <br />
                      Please use the Zelle ID below.
                    </p>
                  </div>
                )}
              </div>

              {/* Zelle ID */}
              <div className="text-center border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Or send to Zelle ID:</p>
                <p className="font-semibold text-[#6d1ed4] dark:text-[#a855f7] select-all">{ZELLE_ID}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleNextStep}
                disabled={qrLoading}
                className="flex-1 py-2.5 px-4 bg-[#6d1ed4] hover:bg-[#5a18b0] text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {qrLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Show QR Code
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                disabled={isLoading}
                className="py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading || allCoursesAlreadySubmitted}
                className="flex-1 py-2.5 px-4 bg-[#6d1ed4] hover:bg-[#5a18b0] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : allCoursesAlreadySubmitted ? (
                  'Already submitted'
                ) : (
                  'I have paid with Zelle'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Wrapper component that handles portal and animated transitions
// State resets automatically when ZelleModalContent unmounts/remounts
export default function ZelleModal({
  isOpen,
  onClose,
  onConfirm,
  totalAmount,
  isLoading,
  studentId,
  studentCourseIds,
  allCoursesAlreadySubmitted,
  zIndex = 200,
}) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex }}>
        <ZelleModalContent
          onClose={onClose}
          onConfirm={onConfirm}
          totalAmount={totalAmount}
          isLoading={isLoading}
          studentId={studentId}
          studentCourseIds={studentCourseIds}
          allCoursesAlreadySubmitted={allCoursesAlreadySubmitted}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
